/**
 * Health Connect sync (Phase 9 increment 3, app side — Capacitor 8 / full metrics).
 *
 * Android-native only. Reads on-device Health Connect data via
 * @capgo/capacitor-health (v8), normalizes it to our ingest vocabulary, and
 * POSTs a batch to /api/health/ingest (authed by the app's Bearer token).
 *
 * The v8 plugin exposes the full set the twin wants, so we read steps, distance,
 * active/total calories, floors, sleep, weight, resting HR, HRV, SpO2, and VO2max —
 * each mapped to the SAME entry_types the Fitbit/Oura/Withings adapters feed (the
 * server owns that mapping). This is also the path Garmin data takes on Android:
 * Garmin Connect writes to Health Connect, and we read it here.
 */
import { Capacitor } from '@capacitor/core';
import { Health } from '@capgo/capacitor-health';
import apiClient from './api';

export const healthConnectSupported = () => {
  try {
    return Capacitor?.isNativePlatform?.() === true && Capacitor.getPlatform() === 'android';
  } catch {
    return false;
  }
};

// Our ingest metric key ← plugin HealthDataType, plus how to collapse a day's
// samples: 'sum' for cumulative deltas (steps, sleep minutes), 'last' for point
// readings (take the most recent that day).
const METRICS = [
  // Activity components — the server merges these into one daily_activity row.
  { metric: 'steps', dataType: 'steps', agg: 'sum' },
  { metric: 'distance', dataType: 'distance', agg: 'sum' },          // metres → km server-side
  { metric: 'active_calories', dataType: 'calories', agg: 'sum' },   // kcal burned
  { metric: 'total_calories', dataType: 'totalCalories', agg: 'sum' },
  { metric: 'floors', dataType: 'flightsClimbed', agg: 'sum' },
  { metric: 'sleep', dataType: 'sleep', agg: 'sum' },
  { metric: 'weight', dataType: 'weight', agg: 'last' },
  { metric: 'resting_heart_rate', dataType: 'restingHeartRate', agg: 'last' },
  { metric: 'hrv', dataType: 'heartRateVariability', agg: 'last' },
  { metric: 'spo2', dataType: 'oxygenSaturation', agg: 'last' },
  { metric: 'vo2max', dataType: 'vo2Max', agg: 'last' },
];

const READ_TYPES = METRICS.map((m) => m.dataType);

// Device-local calendar day (YYYY-MM-DD) for a sample's start instant.
function localDay(iso) {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Collapse samples to one value per local day per the aggregation mode.
// For cumulative ('sum') metrics we dedupe by ORIGIN app: if two apps (say Garmin
// AND Samsung) both wrote steps for a day, summing across them double-counts — so
// we keep only the single source that contributed the most that day.
function aggregateByDay(samples, agg) {
  if (agg === 'sum') {
    const byDaySource = {}; // day -> sourceId -> running sum
    for (const s of samples || []) {
      const value = Number(s.value);
      if (!Number.isFinite(value)) continue;
      const day = localDay(s.startDate);
      const src = s.sourceId || s.sourceName || 'unknown';
      if (!byDaySource[day]) byDaySource[day] = {};
      byDaySource[day][src] = (byDaySource[day][src] || 0) + value;
    }
    const acc = {};
    for (const [day, sources] of Object.entries(byDaySource)) {
      acc[day] = { value: Math.max(...Object.values(sources)) };
    }
    return acc;
  }
  const acc = {}; // day -> { value, ts } — 'last' keeps the most recent that day
  for (const s of samples || []) {
    const value = Number(s.value);
    if (!Number.isFinite(value)) continue;
    const day = localDay(s.startDate);
    const ts = new Date(s.startDate).getTime();
    if (!acc[day] || ts >= acc[day].ts) {
      acc[day] = { value, ts };
    }
  }
  return acc;
}

/**
 * Read the last `days` of all supported metrics from Health Connect and push
 * them to the backend. Returns {granted, records, written, skipped, note?}.
 * Throws with a user-readable message if Health Connect is unavailable.
 */
export async function syncHealthConnect({ days = 30 } = {}) {
  const availability = await Health.isAvailable();
  if (!availability.available) {
    const reason = availability.reason ? ` (${availability.reason})` : '';
    throw new Error(
      `Health Connect isn't available on this device${reason}. On Android 13 and ` +
      `below you may need to install "Health Connect" from the Play Store.`
    );
  }

  const status = await Health.requestAuthorization({ read: READ_TYPES });
  const granted = new Set(status.readAuthorized || []);
  if (granted.size === 0) {
    return { granted: [], records: 0, written: 0, skipped: [], note: 'No Health Connect permissions were granted.' };
  }

  const end = new Date();
  const start = new Date(end.getTime() - days * 24 * 60 * 60 * 1000);
  const startDate = start.toISOString();
  const endDate = end.toISOString();

  const records = [];
  for (const { metric, dataType, agg } of METRICS) {
    if (!granted.has(dataType)) continue;
    let samples = [];
    try {
      ({ samples } = await Health.readSamples({ dataType, startDate, endDate, limit: 5000 }));
    } catch (e) {
      // A single unreadable type shouldn't sink the whole sync.
      continue;
    }
    const byDay = aggregateByDay(samples, agg);
    const roundToInt = metric === 'steps' || metric === 'floors';
    for (const [date, { value }] of Object.entries(byDay)) {
      records.push({ metric, value: roundToInt ? Math.round(value) : value, date });
    }
  }

  if (records.length === 0) {
    return {
      granted: [...granted],
      records: 0,
      written: 0,
      skipped: [],
      note: 'No matching data found in Health Connect for the last 30 days.',
    };
  }

  const resp = await apiClient.post('/api/health/ingest', { records });
  return { granted: [...granted], records: records.length, ...resp.data };
}
